<template>
  <div v-if="hasClosePpaAccess && isTypePpaAppraisal">
    <div class="quick-action">
        <button class="button" @click="onRequestClosePpa()" title="Click to reject PPA">
            <b-icon pack="fal" icon="times-circle" custom-size="fa-lg"></b-icon>
        </button>
    </div>
    <portal to="global-modal-portal" v-if="isClosePpaConfirmationActive" :disabled="!usePortal">
        <b-modal :active.sync="isClosePpaConfirmationActive" scroll="keep" :has-modal-card="true" :canCancel="false">
          <div class="modal-card">
            <form data-vv-scope="ppa-rejected-form">
              <header class="modal-card-head">
                <div class="columns" style="width: 100%">
                  <div class="column is-8 is-pulled-left">
                    <span class="modal-card-title">{{modalTitle}}</span>
                  </div>
                  <div class="column is-4">
                    <a class="tag is-primary is-pulled-right" @click.stop.prevent="onStockNoClick(inventoryItem)">
                      <span class="has-text-weight-semibold">
                        {{ inventoryItem.stockNo }}
                      </span>
                    </a>
                  </div>
                </div>
              </header>
              <section class="modal-card-body">
                <error-display-component :serverErrors="serverErrors" :serverErrorMessage="serverErrorMessage"></error-display-component>
                <div class="columns is-mobile is-multiline">
                  <div class="column is-12">
                    <div class="notification is-flex is-warning">
                      <span><i class="fas fa-exclamation-triangle is-warning fa-2x"></i></span>
                      <span class="ml-10">
                        {{modalContent}}
                      </span>
                    </div>
                  </div>
                  <div class="column is-12">
                    <note-capture v-model="notes" label="Reject Reason" :startWith="startWithLeadStatus"></note-capture>
                    <pop-fill-control v-tooltip="'Click to use this value as the reject reason. This is the dealer peak lead status.'" :fillableValue="leadStatusValue" v-if="leadStatusValue && !canClearRejectReason" :onFillAction="onFillNotes"></pop-fill-control>
                    <a v-if="canClearRejectReason" class="is-pulled-right" style="clear: both; cursor: pointer; margin-top: 3px;" v-tooltip="'Clear the reject reason'" @click="onClearNotes">
                      <b-icon pack="fas" icon="eraser" size="is-small"></b-icon>
                    </a>
                    <span v-show="showNoteRequiredError" class="help is-danger"><i class="fas fa-exclamation-triangle"></i> A reject reason is required.</span>
                  </div>
                  <div class="column is-12" v-if="hasCustomerInformation">
                    <div class="field">
                      <label class="label">{{ notificationLabel }}</label>
                      <div class="control">
                        <b-switch type="is-info" v-model="sendNotification"></b-switch>
                      </div>
                    </div>
                  </div>
                  <div class="column is-12" v-if="sendNotification && hasSetRejectorAccess">
                      <div class="field">
                        <label class="label">
                          Rejected by
                          <b-icon pack="fas" icon="star" class="fa-ss"></b-icon>
                        </label>
                        <div class="control is-fullwidth">
                            <multiselect @select="$markDirty()"
                            :showLabels="false"
                            name="rejectedBy"
                            data-vv-scope="ppa-rejected-form"
                            data-vv-validate-on="input|close"
                            :data-vv-as="'rejected by'"
                            v-validate="{'required': true}"
                            :class="{'is-danger': errors.has('ppa-rejected-form.rejectedBy') }"
                            v-model="selectedManager"
                            :options="availableUsers"
                            label="fullName"
                            trackBy="id"
                            :hideSelected="true"></multiselect>
                        </div>
                        <span v-show="errors.has('ppa-rejected-form.rejectedBy')" class="help is-danger"><i v-show="errors.has('ppa-rejected-form.rejectedBy')" class="fas fa-exclamation-triangle"></i> {{ errors.first('ppa-rejected-form.rejectedBy') }}</span>
                        <pop-fill-control v-tooltip="'Click to use current user'" :fillableValue="currentUserName" v-if="currentUserName" :onFillAction="onFillSelectedManagerWithCurrentUser"></pop-fill-control>
                        <pop-fill-control v-tooltip="'Click to use buyer'" :fillableValue="buyerName" v-if="buyerName" :onFillAction="onFillSelectedManagerWithBuyer" :floatValue="'left'"></pop-fill-control>
                      </div>
                  </div>
                  <div class="column is-12" v-if="sendNotification">
                    <div class="field">
                      <label class="label">{{ proposedCostLabel }}</label>
                      <div class="control">
                        <b-switch type="is-info" v-model="includeCostAmount"></b-switch>
                      </div>
                    </div>
                  </div>
                  <div class="column is-12" v-if="sendNotification">
                    <textarea data-vv-scope="ppa-rejected-form" name="notification" data-vv-as="notification" v-validate="{'required': true}" :class="{'textarea': true, 'is-danger': errors.has('ppa-rejected-form.notification')}" type="text" v-model="notificationText" maxlength="500"></textarea>
                    <span v-show="errors.has('ppa-rejected-form.notification')" class="help is-danger"><i v-show="errors.has('ppa-rejected-form.notification')" class="fas fa-exclamation-triangle"></i> {{ errors.first('ppa-rejected-form.notification') }}</span>
                  </div>
                </div>
              </section>
            </form>
          <footer class="modal-card-foot">
            <button class="button is-warning" :class="{'is-loading': inProgress }" v-if="!closedSuccessfully" @click="onValidate()">Confirm</button>
            <button class="button is-danger" @click="onCancel">{{closedSuccessfully ? "Close" : "Cancel"}}</button>
          </footer>
          </div>
        </b-modal>
    </portal>
  </div>
</template>

<script>

import _ from 'lodash'
import eventBus from '@/eventBus'
import inventoryService from '../../services/inventoryService'
import ErrorDisplayComponent from '@/components/generic/ErrorDisplayComponent'
import { createNamespacedHelpers } from 'vuex'
import utilitiesMixin from '@/mixins/generic/utilities'
import NoteCapture from '@/components/generic/NoteCapture'
import dealerPeakService from '@/services/dealerPeakService'
import PopFillControl from '@/components/generic/PopFillControl'
const mapConfigGetters = createNamespacedHelpers('config').mapGetters
const mapUserGetters = createNamespacedHelpers('user').mapGetters
const mapDealerState = createNamespacedHelpers('dealer').mapState
import validateBeforeSubmitMixin from '@/mixins/generic/validateBeforeSubmit'

export default {
  name: 'ClosePpaAction',
  mixins: [utilitiesMixin, validateBeforeSubmitMixin],
  props: {
    inventoryItem: {
      type: Object,
      default: null
    },
    usePortal: {
      type: Boolean,
      default: true
    },
    leadStatus: {
      type: String,
      default: ''
    },
    syncLeadStatus: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      isClosePpaConfirmationActive: false,
      closedSuccessfully: false,
      isSaving: false,
      serverErrors: [],
      serverErrorMessage: '',
      instanceId: this.$uuid.v4(),
      closedPpaItem: null,
      notes: [],
      sendNotification: true,
      customer: null,
      leadStatusValue: undefined,
      isSyncing: false,
      notificationText: '',
      includeCostAmount: true,
      startWithLeadStatus: 'PPA was rejected!',
      selectedManager: null,
      buyer: null,
      showNoteRequiredError: false
    }
  },
  components: {
    'error-display-component': ErrorDisplayComponent,
    'note-capture': NoteCapture,
    'pop-fill-control': PopFillControl
  },
  computed: {
    ...mapConfigGetters(['definedTypes']),
    ...mapUserGetters(['hasFeatureAccess', 'currentUser', 'lastActiveListPage', 'allUsers']),
    ...mapDealerState(['currentDealer']),
    hasClosePpaAccess: function () {
      let allowedPermissions = ['inventory.modify.mark-ppaclosed']
      var userHasAccess = false
      allowedPermissions.forEach((x) => {
        userHasAccess = userHasAccess || this.hasFeatureAccess(x)
      })
      return userHasAccess
    },
    hasSetRejectorAccess: function () {
      let allowedPermissions = ['inventory.modify.mark-ppaclosed-set-rejector']
      var userHasAccess = false
      allowedPermissions.forEach((x) => {
        userHasAccess = userHasAccess || this.hasFeatureAccess(x)
      })
      return userHasAccess
    },
    isTypePpaAppraisal: function () {
      return this.inventoryItem && this.inventoryItem.type === 'ForPpaAppraisal'
    },
    inventoryItemId: function () {
      return this.inventoryItem ? this.inventoryItem.id : null
    },
    modalTitle () {
      return this.closedSuccessfully ? 'Offer rejected successfully' : 'Reject Offer'
    },
    modalContent () {
      return this.closedSuccessfully ? 'The offer has been successfully rejected.' : 'You are rejecting this offer ' + this.amountText + ' for this vehicle. Please confirm this action.'
    },
    amountText () {
      return this.proposedPaymentAmount !== '-' ? 'of ' + this.proposedPaymentAmount : ''
    },
    proposedPaymentAmount: function () {
      if (this.inventoryItem && this.inventoryItem.proposedCost > 0) {
        return this.$formatCurrency(this.inventoryItem.proposedCost)
      }
      return '-'
    },
    hasCustomerInformation: function () {
      if (this.inventoryItem && this.inventoryItem.purchasedFromName) {
        return true 
      }
      return false
    },
    notificationLabel: function () {
      if (this.hasCustomerInformation === true) {
        return 'Send customer, ' + this.inventoryItem.purchasedFromName + ', a notification of the cancellation?'
      }
      return 'Send customer a notification of the cancellation?'
    },
    inventoryDetails () {
      return this.inventoryItem && this.inventoryItem.vehicleInfo ? this.inventoryItem.vehicleInfo : this.inventoryItem.name
    },
    currentUserName: function () {
      if (this.currentUser) {
        return this.currentUser.fullName
      }

      return ''
    },
    inProgress: function () {
      if (this.isSaving === true) {
        return true
      }
      if (this.isSyncing === true) {
        return true
      }
      return false
    },
    proposedCostLabel: function () {
      if (this.isTrade) {
        return 'Include trade cost amount'
      }
      return 'Include cost amount'
    },
    isTrade: function () {
      return this.inventoryItem && this.inventoryItem.ppaIntention === 'Trade'
    },
    isConsignment: function () {
      return this.inventoryItem && this.inventoryItem.ppaIntention === 'Consigned'
    },
    rejectReasonText: function () {
      // note-capture emits objects shaped { note, isHotNote, pinned, instanceName }; the latest one holds the
      // current textarea value. onFillNotes also pushes a bare lead-status string, which is ignored here.
      if (!this.notes || this.notes.length === 0) {
        return ''
      }
      let noteObjects = this.notes.filter((item) => item && typeof item === 'object' && item.note !== undefined)
      if (noteObjects.length === 0) {
        return ''
      }
      return noteObjects[noteObjects.length - 1].note
    },
    canClearRejectReason: function () {
      return !!this.leadStatusValue && this.rejectReasonText === this.leadStatusValue
    },
    hasCostAmount: function() {
      if (this.isTrade) {
        if (this.inventoryItem.proposedTradeAllowance && this.inventoryItem.proposedTradeAllowance > 0) {
          return true
        }
      } else {
        if (this.inventoryItem.proposedCost && this.inventoryItem.proposedCost > 0) {
          return true
        }
      }
      return false
    },
    availableUsers: function () {
      if (this.currentDealer) {
        const PPA_APPROVAL_ROLE = 'PPA Approval'
        const SALESMAN_ROLE = 'Salesman Role'

        let dealerUsers = this.allUsers.filter((x) => x.dealerId === this.currentDealer.id)

        let ppaApprovalUsers = dealerUsers.filter(function (user) {
          var found = user.roles.filter((y) => y.name.indexOf(PPA_APPROVAL_ROLE) >= 0).length > 0
          return found
        }, this)

        let salesmanUsers = dealerUsers.filter(function (user) {
          var found = user.roles.filter((y) => y.name.indexOf(SALESMAN_ROLE) >= 0).length > 0
          return found
        }, this)

        let combinedUsers = []
        combinedUsers = combinedUsers.concat(ppaApprovalUsers)
        combinedUsers = combinedUsers.concat(salesmanUsers)
        let uniqueUsers = _.uniqBy(combinedUsers, 'id')

        uniqueUsers = _.orderBy(uniqueUsers, ['fullName'], ['asc'])

        return uniqueUsers
      }
      return []
    },
    buyerName: function () {
      if (this.buyer) {
        return this.buyer.fullName
      }
      return ''
    }
  },
  methods: {
    onRequestClosePpa: function () {
      this.serverErrorMessage = ''
      this.serverErrors = []
      this.setNotificationText()
      this.isClosePpaConfirmationActive = true
      if (this.inventoryItem && this.inventoryItem.purchasedFromType === 'Individual' && this.syncLeadStatus === true) {
        this.syncCustomer()
      }
      if (this.hasCostAmount === false) {
        this.includeCostAmount = false
      }
      if (this.isConsignment) {
        this.includeCostAmount = false
      }
      if (this.leadStatus && !this.leadStatusValue) {
        this.leadStatusValue = this.leadStatus
      }
      if (this.inventoryItem && this.inventoryItem.buyerId && this.inventoryItem.buyerId > 0) {
        let buyerId = this.inventoryItem.buyerId
        let manager = this.allUsers.find((x) => x.id === buyerId)
        if (manager) {
          this.selectedManager = {id: manager.id, fullName: manager.fullName, dealerId: this.inventoryItem.dealerId}
          this.buyer = {id: manager.id, fullName: manager.fullName, dealerId: this.inventoryItem.dealerId}
        }
      }
      // Fall back to the PPA approver when no buyer resolved a manager
      if (!this.selectedManager && this.inventoryItem && this.inventoryItem.ppaApprovedById && this.inventoryItem.ppaApprovedById > 0) {
        let ppaApprovedById = this.inventoryItem.ppaApprovedById
        let approver = this.allUsers.find((x) => x.id === ppaApprovedById)
        if (approver) {
          this.selectedManager = {id: approver.id, fullName: approver.fullName, dealerId: this.inventoryItem.dealerId}
        }
      }
      // Fall back to the inventory record's creator when no buyer or approver resolved a manager
      if (!this.selectedManager && this.inventoryItem && this.inventoryItem.createdById && this.inventoryItem.createdById > 0) {
        let createdById = this.inventoryItem.createdById
        let creator = this.allUsers.find((x) => x.id === createdById)
        if (creator) {
          this.selectedManager = {id: creator.id, fullName: creator.fullName, dealerId: this.inventoryItem.dealerId}
        }
      }
    },
    onSave: function () {
      this.showNoteRequiredError = false
      let hasNote = this.notes && this.notes.some((item) => item && item.note && item.note.trim().length > 0)
      if (!hasNote) {
        this.showNoteRequiredError = true
        return
      }
      this.serverErrorMessage = ''
      this.serverErrors = []
      this.isSaving = true
      let notificationText = this.sendNotification === true ? this.notificationText : ''
      this.inventoryItem.notes = []
      this.notes.forEach(function (item) {
        if (item.note) {
          var note = {
            note: item.note,
            status: 'Active',
            isHotNote: item.isHotNote,
            pinned: item.pinned
          }
          this.inventoryItem.notes.push(note)
        }
      }, this)
      this.inventoryItem.sendPpaClosedNotification = this.sendNotification
      this.inventoryItem.notification = notificationText
      this.inventoryItem.ppaRejectorId = this.selectedManager ? this.selectedManager.id : null
      inventoryService.closePpa(this.inventoryItem).then(response => {
        if (response.errors) {
          this.serverErrors = response.errors
        } else {
          this.closedPpaItem = response
          this.successToast('Offer rejected successfully!')
          this.closedSuccessfully = true
          // use emit here so that if we in inventory edit screen we dont get prompted with changes alert
          eventBus.$emit('on-changes-cancelled')
          eventBus.$emit('on-ppa-cancelled')
          this.onCancel()
        }
        this.isSaving = false
      }).catch((error) => {
        this.serverErrorMessage = error.message
        this.isSaving = false
      })
    },
    onCancel: function () {
      if (this.closedSuccessfully && this.closedPpaItem) {
        eventBus.$emit('inventory-changed', [this.closedPpaItem])
        eventBus.$emit('close-global-quick-view', {})
        this.$router.push({name: this.lastActiveListPage !== '' ? this.lastActiveListPage : 'ListInventory'})
      }
      this.isClosePpaConfirmationActive = false
    },
    reset: function () {
      this.closedSuccessfully = false
      this.closedPpaItem = null
      this.serverErrors = []
      this.serverErrorMessage = []
      this.leadStatusValue = undefined
      this.notificationText = ''
      this.showNoteRequiredError = false
    },
    onStockNoClick: function (inventoryItem) {
      let that = this

      this.$copyText(inventoryItem.stockNo).then(
        function (e) {
          that.$buefy.toast.open({
            duration: 2500,
            message: 'Stock No. ' + inventoryItem.stockNo + ' copied to clipboard',
            position: 'is-top',
            type: 'is-info'
          })
        }, function (e) {
          that.$buefy.toast.open({
            duration: 2500,
            message: 'Could not copy stock no. to clipboard',
            position: 'is-top',
            type: 'is-warning'
          })
        }
      )
    },    
    syncCustomer: async function () {
      this.syncingFailed = false
      this.isSyncing = true
      let customerEmail = this.inventoryItem.purchasedFromContactEmail
      let customerMobile = this.inventoryItem.purchasedFromContactMobile
      let customerId = this.inventoryItem.purchasedFromId
      let model = {
        number: this.unmask(customerMobile),
        email: customerEmail,
        customerType: 'Lead',
        customerId: customerId,
        source: 'LIVE',
        clearExistingLeads: true,
        openLeadsOnly: true
      }
      try {
        let response = await dealerPeakService.syncCustomer(model)
        if (response.data !== null) {
          this.customer = response.bestLiveMatch
          this.setLeadStatus()
        }
        this.isSyncing = false
      } catch (err) {
        this.syncingFailed = true
        this.isSyncing = false
      }
    },
    setLeadStatus: function () {
      if (this.customer && this.customer.customerLeads && this.inventoryItem && this.inventoryItem.id) {
        const leadWithMatchingId = this.customer.customerLeads.find(lead => lead.trades?.some(trade => trade.inventory?.id === this.inventoryItem.id))
        if (leadWithMatchingId) {
          this.leadStatusValue = leadWithMatchingId.leadStatus
        }
      }      
    },
    onFillNotes: function () {
      let leadValue = this.leadStatusValue
      if (leadValue) {
        if (!this.notes.includes(leadValue)) {
          this.notes.push(leadValue)
        }
        this.startWithLeadStatus = leadValue
      }
    },
    onClearNotes: function () {
      // Drive the note-capture textarea empty via its startWith prop (it watches startWith and re-emits notes).
      this.startWithLeadStatus = ''
      this.notes = []
    },
    setNotificationText: function () {
      let result = ''
      let userName = this.currentUserName
      if (this.hasSetRejectorAccess) {
        userName = this.selectedManager !== null ? this.selectedManager.fullName : ''
      }
      if (this.sendNotification) {
        if (this.isConsignment) {
          if (this.includeCostAmount) {
            let proposeTradeAmountText = this.getProposedCostText()
            if (userName.length > 0) {
              result = 'Please note the consignment for the ' + this.inventoryDetails + ' for the amount of ' + proposeTradeAmountText + '  has been cancelled and the commitment has been withdrawn by ' + userName + '. If you have any other interest, contact us to continue.'
            } else {
              result = 'Please note the consignment for the ' + this.inventoryDetails + ' for the amount of ' + proposeTradeAmountText + '  has been cancelled and the commitment has been withdrawn. If you have any other interest, contact us to continue.'
            }
          } else {
            if (userName.length > 0) {
              result = 'Please note the consignment for the ' + this.inventoryDetails + ' has been cancelled and the commitment has been withdrawn by ' + userName + '. If you have any other interest, contact us to continue.'
            } else {
              result = 'Please note the consignment for the ' + this.inventoryDetails + ' has been cancelled and the commitment has been withdrawn. If you have any other interest, contact us to continue.'
            }
          }
        } else if (this.includeCostAmount === true) {
          let proposeTradeAmountText = this.getProposedCostText()
          if (userName.length > 0) {
            result = 'Please note the trade/purchase for ' + this.inventoryDetails + ' for the amount of ' + proposeTradeAmountText + ' has been cancelled and the commitment has been withdrawn by ' + userName + '. The value may have increased or decreased since we initially appraised it. If you have any other interest, contact us to continue.'
          } else {
            result = 'Please note the trade/purchase for ' + this.inventoryDetails + ' for the amount of ' + proposeTradeAmountText + ' has been cancelled and the commitment has been withdrawn. The value may have increased or decreased since we initially appraised it. If you have any other interest, contact us to continue.'
          }
        } else {
          if (userName.length > 0) {
            result = 'Please note the trade/purchase for ' + this.inventoryDetails + ' has been cancelled and the commitment has been withdrawn by ' + userName + '. The value may have increased or decreased since we initially appraised it. If you have any other interest, contact us to continue.'
          } else {
            result = 'Please note the trade/purchase for ' + this.inventoryDetails + ' has been cancelled and the commitment has been withdrawn. The value may have increased or decreased since we initially appraised it. If you have any other interest, contact us to continue.'
          }
        }
      }
      this.notificationText = result
    },
    getProposedCostText: function () {
      let proposeAmountText = 'N/A'
      if (this.isTrade) {
        if (this.inventoryItem.proposedTradeAllowance && this.inventoryItem.proposedTradeAllowance > 0) {
          proposeAmountText = this.$formatCurrency(this.inventoryItem.proposedTradeAllowance)
        }
      } else {
        if (this.inventoryItem.proposedCost && this.inventoryItem.proposedCost > 0) {
          proposeAmountText = this.$formatCurrency(this.inventoryItem.proposedCost)
        }
      }
      return proposeAmountText
    },
    onValidate: function () {
      this.validateBeforeSubmitScoped('ppa-rejected-form')
    },
    onFillSelectedManagerWithCurrentUser: function () {
      if (this.currentUser) {
        this.selectedManager = {id: this.currentUser.id, fullName: this.currentUser.fullName, dealerId: this.currentUser.dealerId}
      }
    },
    onFillSelectedManagerWithBuyer: function () {
      if (this.buyer) {
        this.selectedManager = this.buyer
      }
    }
  },
  watch: {
    inventoryItemId: function (newValue, oldValue) {
      if (oldValue && newValue) {
        this.reset()
      }
    },
    includeCostAmount: function (newValue, oldValue) {
      if (oldValue !== newValue) {
        this.setNotificationText()
      }
    },
    sendNotification: function (newValue, oldValue) {
      if (newValue === false) {
        this.includeCostAmount = false
      } else {
        this.setNotificationText()
      }
    },
    selectedManager:  function (newValue, oldValue) {
      let oldId = oldValue !== null ? oldValue.id : 0
      let newId = newValue !== null ? newValue.id : 0
      if (oldId !== newId) {
        this.setNotificationText()
      }
    },
    rejectReasonText: function (newValue) {
      if (newValue && newValue.trim().length > 0) {
        this.showNoteRequiredError = false
      }
    }
  }
}

</script>

<style scoped>
  .content ul{
    list-style-type: none;
    margin-top: 0
  }

  .modal-card-head, .modal-card-foot {
    padding: 20px !important
  }

  .modal-card-body {
    padding: 15px !important
  }

  .modal-card-body {
    width: 640px;
    max-width: 100% !important;
    min-width: 100% !important;
    overflow:auto;
    max-height: calc(90vh - 80px);
  }
</style>
